<template>
  <label :class="[
    {
      'nwx-checkbox': true,
      'huge': huge,
      'disabled': disabled,
      'error': error,
    },
    themeClass
  ]"
  >
    <div class="label-text">
      <slot>Checkbox</slot>
    </div>
    <input type="checkbox"
           :value="value"
           :name="name"
           :id="id"
           v-model="localChecked"
           :disabled="disabled"
           @change="change()"
    >
    <span class="checkmark"></span>
  </label>
</template>

<script>
  export default {
    name: 'NwxCheckbox',
    props: {
      theme: {
        type: String,
        default: 'classic',
        validator: (themeVersion) => (['classic', 'pp-modern'].includes(themeVersion)),
      },
      checked: Boolean,
      value: {
        type: String,
        default: '',
      },
      name: {
        type: String,
        default: '',
      },
      id: {
        type: String,
        default: '',
      },
      huge: Boolean,
      error: Boolean,
      disabled: Boolean,
    },
    model: {
      prop: 'checked',
      event: 'checkbox:changed',
    },
    data() {
      return {
        localChecked: this.checked,
        programChangeEnabled: true,
      };
    },
    watch: {
      checked(checked) {
        if (this.programChangeEnabled) {
          this.localChecked = checked;
        }
        this.programChangeEnabled = true;
      },
    },
    methods: {
      change() {
        this.programChangeEnabled = false;
        this.$emit('checkbox:changed', this.localChecked);
        this.$emit('change');
      },
    },
    computed: {
      safeTheme() { // guarantee a safe value
        const allowed = ['classic', 'pp-modern'];
        return allowed.includes(this.theme) ? this.theme : 'classic';
      },
      themeClass() {
        return `theme--${this.safeTheme}`;
      },
    },
  };
</script>

<style lang="less" scoped>
  @import '../assets/variables';

  // classic theme
  .nwx-checkbox {
    display: inline-block;
    position: relative;
    cursor: pointer;
    margin: 0;
    padding: 0;

    .label-text {
      font-weight: 400;
      font-size: 14px;
      line-height: 1.9;
      padding-left: 25px;
      vertical-align: top;
    }

    input {
      cursor: pointer;
      position: absolute;
      opacity: 0;
      height: 0;
      width: 0;

      &:focus ~ .checkmark {
        border-color: @vigilantBlue !important;
      }

      &:checked ~ .checkmark {
        background: @vigilantBlue !important;
        border-color: @vigilantBlue !important;
      }
    }

    .checkmark {
      position: absolute;
      top: 5px;
      left: 0;
      height: 16px;
      width: 16px;
      border: 1px solid @vigilantBlue;
      background: @accessWhite;
      transition: background 0.2s, border 0.2s;

      &:after {
        content: " ";
        display: block;
        width: 100%;
        height: 100%;
        background-image: url('//img.netwrix.com/elements/partnerPortal/pipeline/checkmark.svg');
        background-repeat: no-repeat;
        background-position: center;
        opacity: 0;
      }
    }

    &:not(.error):not(.disabled):hover {
      .checkmark {
        background: #5851db33;
      }
    }

    &.huge {
      .label-text {
        font-size: 16px;
        padding-left: 50px;
      }

      .checkmark {
        top: -3px;
        height: 36px;
        width: 36px;
        border-radius: 3px;

        &:after {
          background-image:
              url('//img.netwrix.com/elements/partnerPortal/pipeline/checkmark_huge.svg');
        }
      }
    }

    &.error {
      .label-text {
        color: @mainRed;
      }

      .checkmark {
        border-color: @mainRed;
      }
    }

    &.disabled {
      cursor: default;

      .label-text {
        color: fade(@mainGray, 80%);
      }

      .checkmark {
        background: fade(@bodyWhite, 60%);
        border-color: fade(@mainGray, 20%);
      }

      input {
        &:checked ~ .checkmark {
          background: fade(@vigilantBlue, 60%);
          border-color: transparent !important;
        }
      }
    }
  }

  // pp-modern theme
  .nwx-checkbox.theme--pp-modern {

    input {
      &:focus ~ .checkmark {
        border-color: @vigilantBlue !important;
      }

      &:checked ~ .checkmark {
        background: @vigilantBlue !important;
        border-color: @vigilantBlue !important;
      }

      &:checked ~ .checkmark:after {
        display: none;
      }
    }
  }

</style>
